import React, {useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import BodySpecialCardItem from '../BodySpecialCardItem';
import {useEngagementId} from '../../../../../../../util/customHooks';
import {labels} from '../../../../../../../util/utils';
import {clientSideEventNames, controlType, entities} from '../../../../../../../util/uiconstants';
import {getItApplicationById, getAllControls} from '../../../../../../../selectors/ISA315/selectors';
import BodySpecialAccordionHeaderColumn from '../BodySpecialAccordionHeaderColumn';
import BodySpecialAccordionContentList from '../BodySpecialAccordionContentList';
import ISA315ItAppScotAccordionContentItem from './ISA315ItAppScotAccordionContentItem';
import ISA315ItAppScotAccordionControlsList from './ISA315ItAppScotAccordionControlsList';
import BodySpecialListItemNoDataPlaceholder from '../BodySpecialListItemNoDataPlaceholder';
import BodySpecialDeleteItemModal from '../BodySpecialDeleteItemModal';
import {deleteItApplication} from '../../../../../../../actions/ITApplication/itapplicationaction';

const ISA315ItAppScotBodyItAppItem = ({bodyId, itApplicationId, onDelete, onDropdownOptionClick}) => {
	const itApplication = useSelector((state) => getItApplicationById(state, itApplicationId)),
		allControls = useSelector(getAllControls);

	const dispatch = useDispatch();
	const engagementId = useEngagementId();
	const [showDeleteModal, setShowDeleteModal] = useState(false);

	const itApplicationRelatedControls = useMemo(() => {
		return allControls?.filter((control) => itApplication.controls.includes(control.id));
	}, [allControls, itApplication]);

	const applicationControls = useMemo(() => {
		return itApplicationRelatedControls?.filter((control) => control.controlTypeId === controlType.itApplicationControl)
			?.length;
	}, [itApplicationRelatedControls]);

	const itdmControls = useMemo(() => {
		return itApplicationRelatedControls?.filter(
			(control) => control.controlTypeId === controlType.itDependentManualControl
		)?.length;
	}, [itApplicationRelatedControls]);

	const otherControls = useMemo(() => {
		return itApplication.controls?.filter(
			(controlId) =>
				!itApplication.scots.some((scot) =>
					itApplicationRelatedControls.find((control) => control.id === controlId)?.scots?.includes(scot)
				)
		);
	}, [itApplicationRelatedControls, itApplication]);

	const editAssociation = () => {
		editMenuOptionClick();
	};

	const editMenuOptionClick = () => {
		onDropdownOptionClick({
			entityId: entities.ItSoApplication,
			entityUId: itApplicationId,
			entityName: itApplication.itApplicationName
		});
	};

	const dropdownOptions = useMemo(() => {
		return [
			{
				name: 'edit',
				render: <div>{labels.editItAppOption}</div>,
				onClick: () => editMenuOptionClick()
			},
			{
				name: 'delete',
				render: <div>{labels.removeItAppOption}</div>,
				onClick: () => {
					setShowDeleteModal(!showDeleteModal);
				}
			}
		];
	}, [itApplication]);

	const config = useMemo(() => {
		return [
			{
				render: (
					<StyledItAppColumn
						className={'ItAppColumnHeader'}
						header={labels.itApplicationHeader}
						value={itApplication.itApplicationName}
					/>
				)
			},
			{
				render: (
					<StyledScotCountColumn
						className={'ScotCountColumnHeader'}
						header={labels.scotHeader}
						value={itApplication.scots?.length || '-'}
					/>
				)
			},
			{
				header: labels.controlsHeader,
				className: 'primary-entity__child-entity ControlsCountColumnHeader',
				render: [
					{
						render: (
							<StyledApplicationControlsColumnLeft
								header={labels.controlsApplicationHeader}
								value={applicationControls || '-'}
							/>
						)
					},
					{
						render: (
							<StyledApplicationControlsColumnRight header={labels.controlsITDMHeader} value={itdmControls || '-'} />
						)
					}
				]
			}
		];
	}, [itApplication, applicationControls, itdmControls]);

	const columns = React.useMemo(() => {
		return [
			{title: labels.scotAndRelatedControls, className: 'isa315-itapp-scot-body-itapp-item__first-column'},
			{title: labels.strategy, className: 'isa315-itapp-scot-body-itapp-item__second-column'},
			{title: labels.controlTypeLabel, className: 'isa315-itapp-scot-body-itapp-item__third-column'}
		];
	}, []);

	const scotsCount = itApplication.scots?.length || 0;
	const controlsCount = itApplication.controls?.length || 0;

	return (
		<StyledISA315ItAppScotBodyItAppItem
			key={itApplicationId}
			className="StyledISA315ItAppScotBodyItAppItem isa315-itapp-scot-body-itapp-item"
		>
			<BodySpecialCardItem
				accordion
				config={config}
				dropdownOptions={dropdownOptions}
				disabled={scotsCount === 0 && controlsCount === 0}
				className={scotsCount === 0 && controlsCount === 0 ? 'noChildrenAvailable' : ''}
			>
				{scotsCount > 0 && (
					<StyledScotsAccordionContentList columns={columns}>
						{itApplication.scots.map((scot) => {
							return (
								<ISA315ItAppScotAccordionContentItem itApplicationControls={itApplication.controls} scotId={scot} />
							);
						})}
					</StyledScotsAccordionContentList>
				)}
				{otherControls?.length > 0 && (
					<BodySpecialAccordionContentList title="Other Controls">
						<ISA315ItAppScotAccordionControlsList controlIds={otherControls} bodyId={bodyId} />
					</BodySpecialAccordionContentList>
				)}
			</BodySpecialCardItem>
			{scotsCount === 0 && controlsCount === 0 && (
				<BodySpecialListItemNoDataPlaceholder
					className="noDataPlaceholder"
					onPlaceholderClickConfig={{noScotsOrControlsPlaceholderEditAssoc: editAssociation}}
					placeholderText={labels.noScotsOrControlsPlaceholder}
				/>
			)}
			{showDeleteModal && (
				<BodySpecialDeleteItemModal
					title={labels.removeItAppOption}
					name={itApplication.itApplicationName}
					show={showDeleteModal}
					onHide={() => setShowDeleteModal(false)}
					onDelete={() => {
						dispatch(deleteItApplication(engagementId, bodyId, itApplicationId)).then(() => {
							window.dispatchEvent(new CustomEvent(clientSideEventNames.getValidations));
							onDelete && onDelete();
						});
					}}
					setIsLoading={() => {}}
				/>
			)}
		</StyledISA315ItAppScotBodyItAppItem>
	);
};

ISA315ItAppScotBodyItAppItem.propTypes = {
	itApplicationId: PropTypes.number.isRequired
};
ISA315ItAppScotBodyItAppItem.defaultProps = {
	itApplicationId: 0
};

export default ISA315ItAppScotBodyItAppItem;

const StyledScotsAccordionContentList = styled(BodySpecialAccordionContentList)`
	border-bottom: none;
`;

const StyledItAppColumn = styled(BodySpecialAccordionHeaderColumn)`
	width: calc(100% - var(--px-350));
`;

const StyledScotCountColumn = styled(BodySpecialAccordionHeaderColumn)`
	padding: var(--px-30) var(--px-30) var(--px-18) var(--px-50) !important;
	width: var(--px-150);
	height: var(--px-87);
	line-height: var(--px-24);
`;

const StyledApplicationControlsColumnLeft = styled(BodySpecialAccordionHeaderColumn)`
	width: var(--px-120);
	padding: 0 var(--px-10) 0 0 !important;
`;

const StyledApplicationControlsColumnRight = styled(BodySpecialAccordionHeaderColumn)`
	width: var(--px-80);
	padding: 0 0 0 var(--px-10) !important;
`;

const StyledISA315ItAppScotBodyItAppItem = styled.section`
	margin-bottom: var(--px-20);

	&:last-child {
		margin-bottom: var(--px-10);
	}

	.StyledBodySpecialAccordionContentList {
		& + .StyledBodySpecialAccordionContentList {
			padding-top: 0;
		}
	}

	.isa315-itapp-scot-body-itapp-item__first-column {
		display: inline-block;
		width: 82%;
		padding-bottom: var(--px-10);
		.bodySpecialAccordionContentListHeaderText {
			cursor: text;
		}
	}

	.isa315-itapp-scot-body-itapp-item__second-column {
		display: inline-block;
		width: var(--px-150);
		padding-bottom: var(--px-10);
		.bodySpecialAccordionContentListHeaderText {
			cursor: text;
		}
	}

	.isa315-itapp-scot-body-itapp-item__third-column {
		display: inline-block;
		width: var(--px-150);
		padding-bottom: var(--px-10);
		.bodySpecialAccordionContentListHeaderText {
			cursor: text;
		}
	}

	.primary-entity__child-entity {
		width: var(--px-200);
		font-size: var(--px-12) !important;
		font-weight: 300 !important;
	}

	.noDataPlaceholder {
		max-width: 100%;
		padding: var(--px-30) var(--px-20);
		padding-left: var(--px-45);
		border: var(--px-1) solid var(--neutrals-200);
		border-top: none;
		border-radius: var(--px-5);
		background-color: var(--neutrals-100);
		.${(props) => props.placeholderClassName} {
			font-size: var(--px-16) !important;
			a {
				vertical-align: middle;
				font-weight: bold;
				cursor: pointer;
			}
		}
		a {
			font-weight: bold;
		}
	}
`;
